import type { RunMessageOutcome, RunSummary, TaskDevServers } from '@shared/types/factory';
import type { DevProgress } from '@shared/dev-servers';
import { formatRelative } from '@/lib/formatters';
import type { StatusMeta } from '@/components/status-tone';
import { isRunActive } from './run-status';

/** SF-T-209: live runs' and dev servers' rules for the Agent tab, the task's dev servers and the Board's overview. */

/** The run the Agent tab shows: the task's newest that has a thread. */
export function agentTabRun(runs: readonly RunSummary[] | undefined): RunSummary | null {
  return runs?.find((r) => r.t3_thread_id) ?? null;
}

/** Why Queue can't go to this run now, or null when it can. */
export function queueBlockedReason(run: RunSummary): string | null {
  if (!isRunActive(run.status)) return null;
  if (run.live) return null;
  return `R-${run.number} isn't live, so it can't hold a message for later: Steer it while the agent works, or Queue once the run ends (a Follow up).`;
}

/**
 * Why the Agent tab can't write to the run's agent, from the last read of its thread (an error code), or null
 * when it can: the machine is offline, or T3 Code doesn't have the thread any more.
 */
export function agentReadOnlyReason(run: Pick<RunSummary, 'number' | 'machine'>, readError: string | null): string | null {
  if (readError === 'MACHINE_OFFLINE') return `${run.machine?.name ?? "R-" + run.number + "'s machine"} isn't connected, so its agent can't be written to now.`;
  if (readError === 'THREAD_NOT_FOUND') return `T3 Code doesn't have R-${run.number}'s thread any more, so its agent can't be written to.`;
  return null;
}

/** What the person sees once a message went. */
export function messageOutcomeText(outcome: RunMessageOutcome): string {
  switch (outcome.outcome) {
    case 'steered':
      return `Sent into R-${outcome.run_number}'s turn`;
    case 'turn':
      return `R-${outcome.run_number}'s agent is on it`;
    case 'queued':
      return `Queued: R-${outcome.run_number}'s agent gets it when this step ends`;
    case 'follow_up':
      return `Started R-${outcome.run_number}, a Follow up on the same thread`;
  }
}

/** How a worktree's dev servers stand, for its badge. */
export function devServersMeta(d: TaskDevServers, progress: DevProgress | null = null): StatusMeta {
  if (d.action?.status === 'running') return { label: actionRunningLabel(d.action.action), tone: 'info' };
  // SF-T-231: done on the machine, not yet shown by its report: still Starting, Stopping or Tidying.
  if (progress?.phase === 'awaiting') return { label: actionRunningLabel(progress.action), tone: 'info' };
  if (!d.exists) return { label: 'Worktree tidied', tone: 'neutral' };
  if (!d.machine.online) return { label: 'Machine offline', tone: 'warning' };
  if (progress?.phase === 'stuck') return { label: STUCK_LABELS[progress.action], tone: 'warning' };
  return d.running ? { label: 'Running', tone: 'success' } : { label: 'Stopped', tone: 'neutral' };
}

const STUCK_LABELS: Record<DevProgress['action'], string> = { start: 'Not listening', stop: 'Still listening', tidy: 'Still there' };

/**
 * SF-T-231: the panel's line once the machine finished an action its report doesn't show yet (`awaiting`), or
 * still doesn't after a while (`stuck`); null otherwise.
 */
export function devProgressLine(d: TaskDevServers, progress: DevProgress | null, now?: number): string | null {
  const a = d.action;
  if (!a || !progress || progress.phase === 'working') return null;
  const m = d.machine.name;
  const when = formatRelative(a.at, now === undefined ? undefined : Math.max(now, Date.parse(a.at)));
  if (progress.phase === 'awaiting') {
    const by = a.by ? ` · ${a.by}` : '';
    if (progress.action === 'start') return `Started on ${m}: waiting for its report to show them listening (it sends one within a minute)${by}.`;
    if (progress.action === 'stop') return `Stopped on ${m}: waiting for its report to show them gone (it sends one within a minute)${by}.`;
    return `${a.message ?? 'Tidied.'} Waiting for ${m}'s report to drop the worktree (it sends one within a minute)${by}.`;
  }
  if (progress.action === 'start') return `${m} started them ${when}, but its reports don't show them listening. Their logs are in logs/task-dev-*.log in the worktree; Start tries again.`;
  if (progress.action === 'stop') return `${m} stopped them ${when}, but its reports still show them listening. Stop tries again.`;
  return `${m} tidied the worktree ${when}, but its reports still list it. Look again in a minute; if it stays, check the machine's dashboard.`;
}

export function actionRunningLabel(action: NonNullable<TaskDevServers['action']>['action']): string {
  return action === 'tidy' || action === 'setup_cleanup' ? 'Tidying' : action === 'start' ? 'Starting' : 'Stopping';
}


/** How many tasks have dev servers running. */
export const runningCount = (list: readonly TaskDevServers[]) => list.filter((d) => d.running).length;



/** SF-T-273: the starts of the run errors a failed Continue in a new worktree leaves (the engine's own words). */
const RESTORE_SETUP_FAILED = "Setting up the thread's new worktree failed";
const RESTORE_REFUSED = "Couldn't make the thread's worktree again";

/**
 * SF-T-273: why a Continue in a new worktree failed, from its run's error: what stopped it (`message`, in plain
 * words) and the setup's output (`log`, empty when there's none); `setup` when it was the setup. Null for other runs.
 */
export function restoreFailure(run: Pick<RunSummary, 'status' | 'error'>): { setup: boolean; message: string; log: string } | null {
  const error = run.status === 'failed' ? (run.error ?? '').trim() : '';
  const setup = error.startsWith(RESTORE_SETUP_FAILED);
  if (!setup && !error.startsWith(RESTORE_REFUSED)) return null;
  const [first = '', ...rest] = error.split('\n');
  return { setup, message: first.trim(), log: rest.join('\n').trim() };
}

/**
 * SF-T-273: what the Agent tab's box says, instead of taking a message, while the thread is stuck on its missing
 * worktree folder: a message would only fail again, so it points at Continue in a new worktree (or the run doing it).
 */
export function folderGoneBoxNote(run: Pick<RunSummary, 'number' | 'status' | 'flow_id' | 'current_step' | 'live_parked_at'>): string {
  if (restoringRun(run)) return `R-${run.number} is making the thread's worktree again, then sends the last message: write here once it's back.`;
  if (isRunActive(run.status)) return `This thread's worktree folder is gone, so a message here would fail, and R-${run.number} is still going: cancel it, then use Continue in a new worktree above.`;
  return "This thread's worktree folder is gone, so a message here would fail. Use Continue in a new worktree above: it makes the folder again and resends the last message.";
}

/** The Follow up flows (Continue in a new worktree starts one, its first send making the folder again). */
const FOLLOW_UP_FLOWS = new Set(['follow-up', 'feedback-follow-up']);

/**
 * SF-T-273: the run is (most likely) a Continue in a new worktree making the folder again: a Follow up still at its
 * start (queued, or before its send has answered). Not a run parked or past that: it isn't making anything, and
 * Continue waits for it to end (the server says to cancel it).
 */
export function restoringRun(run: Pick<RunSummary, 'status' | 'flow_id' | 'current_step' | 'live_parked_at'>): boolean {
  if (!FOLLOW_UP_FLOWS.has(run.flow_id) || run.live_parked_at) return false;
  if (run.status === 'queued') return true;
  return run.status === 'running' && ['start', 'announce', 'send'].includes(run.current_step ?? '');
}

/** SF-T-273: what a task chat's box says while its worktree is gone ('gone') or a turn makes it again ('restoring'). */
export function chatFolderGoneNote(state: 'gone' | 'restoring'): string {
  return state === 'restoring'
    ? "The chat is making the task's worktree again, then sends your last message: write here once it's back."
    : "This chat's task worktree folder is gone, so a message here would fail. Use Continue in a new worktree above: it makes the folder again and resends your last message.";
}
